'use client'

import React, { useEffect, useState } from 'react'
import { Button, Modal, ToastMessage, TextBox } from "@ds/ui"
import { apiFetch, handleApiError } from '../../utils/apiClient';

interface FolderPathModalProps {
  folderName: 'Config' | 'SAInstallation' | 'SALog' | 'IISLog'
  isOpen: boolean
  onClose: () => void
}

const FolderPathModal: React.FC<FolderPathModalProps> = ({ folderName, isOpen, onClose }) => {
  const [showToast, setShowToast] = useState(false)
  const [toastKind, setToastKind] = useState<'success' | 'danger'>('success')
  const [folderPath, setFolderPath] = useState<string>('')
  const [configPaths, setConfigPaths] = useState<string[]>([])

  const showTemporaryToast = (kind: 'success' | 'danger') => {
    setToastKind(kind)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 30000)
  }

  const getFolderPath = async (folderName: 'Config' | 'SAInstallation' | "SALog" | "IISLog"): Promise<string> => {
    const formData = new FormData()
    formData.append('folderName', folderName)
    const response = await apiFetch('Admin/Home/GetFolderPath', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      await handleApiError(response);
    }
    
    const path = await response.json()
    if (folderName === 'Config') {
      setConfigPaths([path.adminWebConfigPath, path.keyManagerWebConfigPath, path.apiWebConfigPath, path.adminAppSettingsPath])
    } else {
      setFolderPath(path.folderPath)
    }
    return path
  }

  const copyToClipboard = async (path: string) => {
    try {
      await navigator.clipboard.writeText(path)
      showTemporaryToast('success')
    } catch (err) {
      console.error('Failed to copy: ', err)
      showTemporaryToast('danger')
    }
  }

  useEffect(() => {
    getFolderPath(folderName)
  }, [folderName])

  return (
    <>
      {showToast && (
        <ToastMessage
          kind={toastKind}
          visible={showToast}
        >
          {toastKind === 'success' 
            ? "The path has been copied to your clipboard."
            : "Please try copying the path manually."}
        </ToastMessage>
      )}

      <Modal
        visible={isOpen}
        onClose={onClose}
        width="small"
      >
        <Modal.Header title="Path" />
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {folderName === 'Config' ? "Copy and paste this path into your file explorer to find the files." : "Copy and paste this path into your file explorer to find the folder."}
            </p>
            
            {folderName === 'Config' ? (
              <div className="space-y-2">
                <div className="text-sm font-medium mb-1">Admin Web Configuration:</div>
                <TextBox
                  label=""
                  value={configPaths[0]}
                  readOnly
                  className="w-full"
                  endElement={
                    <Button
                      kind="secondary"
                      icon="copy"
                      onClick={() => copyToClipboard(configPaths[0])}
                      aria-label="Copy path"
                    />
                  }
                />
                <div className="text-sm font-medium mt-3 mb-1">Key Manager Web Configuration:</div>
                <TextBox
                  label=""
                  value={configPaths[1]}
                  readOnly
                  className="w-full"
                  endElement={
                    <Button
                      kind="secondary"
                      icon="copy"
                      onClick={() => copyToClipboard(configPaths[1])}
                      aria-label="Copy path"
                    />
                  }
                />
                <div className="text-sm font-medium mt-3 mb-1">API Web Configuration:</div>
                <TextBox
                  label=""
                  value={configPaths[2]}
                  readOnly
                  className="w-full"
                  endElement={
                    <Button
                      kind="secondary"
                      icon="copy"
                      onClick={() => copyToClipboard(configPaths[2])}
                      aria-label="Copy path"
                    />
                  }
                />
                <div className="text-sm font-medium mt-3 mb-1">Admin App Settings:</div>
                <TextBox
                  label=""
                  value={configPaths[3]}
                  readOnly
                  className="w-full"
                  endElement={
                    <Button
                      kind="secondary"
                      icon="copy"
                      onClick={() => copyToClipboard(configPaths[3])}
                      aria-label="Copy path"
                    />
                  }
                />
              </div>
              
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-grow">
                  <label className="block text-sm font-medium mb-1">Path</label>
                  <div className="flex items-center gap-2">
                    <TextBox
                      label=""
                      value={folderPath}
                      readOnly
                      className="w-full"
                      endElement={
                        <Button
                          kind="secondary"
                          icon="copy"
                          onClick={() => copyToClipboard(folderPath)}
                          aria-label="Copy path"
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button 
            kind="primary" 
            onClick={onClose} 
            text="Close" 
          />
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default FolderPathModal